<template>
  <b-table
    :tbody-tr-class="getTrClass"
    :id="tableId"
    :ref="tableRef"
    :class="
      isChildTable
        ? 'position-relative table child-table task-table'
        : 'position-relative table task-table'
    "
    :items="items"
    :hover="hover"
    responsive
    :fields="fields"
    primary-key="init_handle"
    :sort-by.sync="localSortBy"
    :sort-desc.sync="localIsSortDirDesc"
    :no-local-sorting="noLocalSorting"
    :busy="busy"
    show-empty
    :empty-text="emptyText"
    :thead-class="isChildTable ? 'd-none' : ''"
    @row-clicked="onRowClicked"
    @update:sort-by="$emit('update:sort-by', $event)"
    @update:sort-desc="$emit('update:sort-desc', $event)"
    :dark="dark"
  >
    <template #table-busy>
      <div
        class="d-flex justify-content-center mb-1 align-items-center"
        style="min-height: 30vh"
      >
        <b-spinner
          style="width: 3rem; height: 3rem"
          type="grow"
          variant="primary"
          label="Loading..."
        />
      </div>
    </template>

    <!-- Header: Select -->
    <template #head(select)="[]">
      <div class="d-flex select-edit-head">
        <b-form-checkbox
          style="text-align: center !important"
          :checked="isAllSelected"
          @change="onSelectAllChange($event)"
        />
      </div>
    </template>

    <!-- Header: Notes -->
    <template #head(notes)="[]">
      <div class="d-flex justify-content-center">
        <feather-icon icon="MessageSquareIcon" size="14" class="dark-muted-text" />
      </div>
    </template>

    <!-- Column: Select -->
    <template #cell(select)="data">
      <div class="d-flex align-items-center select-cell-wrapper" @click.stop>
        <button
          v-if="data.item.is_group"
          @click="toggleGroupDetails(data.item, data.index)"
          class="group-toggle-btn mr-25"
          type="button"
        >
          <feather-icon
            :icon="data.item._showChildren ? 'ChevronDownIcon' : 'ChevronRightIcon'"
            size="16"
            class="align-middle text-body cursor-pointer"
          />
        </button>
        <b-form-checkbox
          :class="{
            'row-checkbox': true,
            'group-row-checkbox': data.item.is_group,
            'child-row-checkbox': data.item.is_child,
          }"
          :checked="isItemSelected(data.item)"
          :indeterminate="
            data.item.is_group &&
            groupSelectionStates[data.item.handle] === 'partial'
          "
          @change="onSelectRowChange($event, data.item)"
        />
      </div>
    </template>

    <!-- Column: Process ID -->
    <template #cell(processId)="data">
      <span class="dark-muted-text font-weight-medium">
        {{ data.item.processHandle || data.item.processId }}
      </span>
    </template>

    <!-- Column: Name (Task) -->
    <template #cell(name)="data">
      <span
        v-if="isTaskNameTruncated(data.item.name)"
        class="font-weight-medium text-body task-name-text"
        v-b-tooltip.hover="data.item.name"
      >
        {{ truncateTaskName(data.item.name) }}
      </span>
      <span v-else class="font-weight-medium text-body task-name-text">
        {{ data.item.name }}
      </span>
    </template>

    <!-- Column: Notes Icon with Hover Preview -->
    <template #cell(notes)="data">
      <div
        v-if="hasNotes(data.item)"
        class="d-inline-flex align-items-center justify-content-center w-100"
        @click.stop
      >
        <feather-icon
          :id="`notes-popover-${getPopoverTargetId(data.item, data.index)}`"
          icon="MessageSquareIcon"
          size="16"
          class="cursor-pointer text-primary"
          tabindex="0"
          @click="onRowClicked(data.item, data.index)"
        />
        <b-popover
          :target="`notes-popover-${getPopoverTargetId(data.item, data.index)}`"
          triggers="hover focus"
          placement="bottom"
          :delay="{ show: 300, hide: 150 }"
          custom-class="notes-preview-popover shadow-sm"
        >
          <div class="notes-popover-content">
            <div
              v-if="getNote(data.item).header"
              class="notes-header"
            >
              {{ getNote(data.item).header }}
            </div>
            <div class="notes-body-text mb-50">
              {{ getNote(data.item).content }}
            </div>
            <div class="pt-50 border-top">
              <a
                href="#"
                class="notes-open-link"
                @click.prevent="openTaskDrawer(data.item, data.index)"
              >
                Open task to see all notes
              </a>
            </div>
          </div>
        </b-popover>
      </div>
    </template>

    <!-- Column: Due Date (Weekday + Date without year for current year, red + alert if Late) -->
    <template #cell(dueDate)="data">
      <div v-if="data.item.is_late" class="d-inline-flex align-items-center text-danger font-weight-bold">
        <span>{{ formatDueDate(data.item.dueDate) }}</span>
        <feather-icon
          icon="AlertCircleIcon"
          size="14"
          class="ml-25 flex-shrink-0"
          v-b-tooltip.hover="'Late'"
        />
      </div>
      <div v-else class="text-body font-weight-medium">
        {{ formatDueDate(data.item.dueDate) }}
      </div>
    </template>

    <!-- Column: Assignee (User) -->
    <template #cell(userId)="data">
      <span class="text-body font-weight-medium">
        {{
          data.item.is_group
            ? data.item.assigneeName || "--"
            : (data.item.assignee && data.item.assignee.name) || "--"
        }}
      </span>
    </template>

    <!-- Column: Role (Role Chip) -->
    <template #cell(role)="data">
      <span
        v-if="getRoleDisplay(data.item)"
        class="role-chip"
        v-b-tooltip.hover="getRoleTooltip(data.item)"
      >
        {{ getRoleDisplay(data.item) }}
      </span>
      <span v-else class="dark-muted-text font-small-2">--</span>
    </template>

    <!-- Column: Last Updated (blank if untouched) -->
    <template #cell(updated_at)="data">
      <span v-if="data.item.updated_at" class="dark-muted-text font-small-3 font-weight-medium">
        {{ formatUpdatedDate(data.item.updated_at) }}
      </span>
      <span v-else></span>
    </template>

    <!-- Column: Status -->
    <template #cell(status)="data">
      <div class="d-flex align-items-center status-container">
        <!-- NotStarted: Plain gray text, no pill -->
        <span
          v-if="data.item.status === 'NotStarted'"
          class="dark-muted-text font-small-3 font-weight-medium"
        >
          Not started
        </span>

        <!-- InProgress: light-info pill -->
        <b-badge
          v-else-if="data.item.status === 'InProgress'"
          pill
          variant="light-info"
          class="text-capitalize"
        >
          In progress
        </b-badge>

        <!-- Complete: light-success pill -->
        <b-badge
          v-else-if="data.item.status === 'Complete'"
          pill
          variant="light-success"
          class="text-capitalize"
        >
          Complete
        </b-badge>

        <!-- Exported: light-warning pill -->
        <b-badge
          v-else-if="data.item.status === 'Exported'"
          pill
          variant="light-warning"
          class="text-capitalize"
        >
          Exported
        </b-badge>

        <!-- Deleted indicator -->
        <b-badge
          v-if="data.item.deleted"
          pill
          variant="light-secondary"
          class="ml-25 text-capitalize"
        >
          Deleted
        </b-badge>
      </div>
    </template>

    <!-- Column: Actions (⋮ Kebab Menu) -->
    <template #cell(actions)="data">
      <div class="d-flex justify-content-end align-items-center" @click.stop>
        <b-dropdown
          variant="link"
          no-caret
          right
          class="row-actions-dropdown"
          append-to-body
          v-if="!data.item.is_group"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="text-body cursor-pointer"
            />
          </template>

          <!-- Mark As 'chosen status' -->
          <b-dropdown-item
            v-for="markAsOption in markAsOptions.filter(
              (option) => data.item.status !== option
            )"
            :key="markAsOption"
            @click="
              onUpdateTask(data.item, data.index, { status: markAsOption })
            "
          >
            <span class="align-middle">
              Mark as
              <b-badge
                pill
                :variant="`light-${resolveTaskStatusVariant(markAsOption)}`"
                class="text-capitalize ml-25"
              >
                {{ splitCamelCaseWords(markAsOption) }}
              </b-badge>
            </span>
          </b-dropdown-item>

          <b-dropdown-divider />

          <!-- Edit Record -->
          <b-dropdown-item @click="onEditRecord(data.item, data.index)">
            <span class="align-middle text-primary">
              <feather-icon icon="EditIcon" size="14" class="mr-50" />
              Edit
            </span>
          </b-dropdown-item>

          <!-- Restore Task -->
          <b-dropdown-item
            v-if="data.item.deleted"
            @click="onDeleteOrRestoreTasks([data.item], true)"
          >
            <span class="align-middle text-info">
              <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
              Restore
            </span>
          </b-dropdown-item>

          <!-- Delete Task -->
          <b-dropdown-item
            v-if="!data.item.deleted"
            @click="onDeleteOrRestoreTasks([data.item], false)"
          >
            <span class="align-middle text-danger">
              <feather-icon icon="TrashIcon" size="14" class="mr-50" />
              Delete
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </template>

    <!-- Bottom row slot for pagination -->
    <template #bottom-row v-if="showBottomRow">
      <slot name="bottom-row"></slot>
    </template>
  </b-table>
</template>

<script>
import {
  BTable,
  BSpinner,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BFormCheckbox,
  BPopover,
  VBTooltip,
  VBPopover,
} from "bootstrap-vue"
import moment from "moment-timezone"
import { formatTaskDueDate, parseLatestNote } from "pbshared"

export default {
  name: "TaskTable",
  components: {
    BTable,
    BSpinner,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormCheckbox,
    BPopover,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-popover": VBPopover,
  },
  props: {
    dark: Boolean,
    showBottomRow: Boolean,
    tableId: {
      type: String,
      default: "taskListTable",
    },
    tableRef: {
      type: String,
      default: "refTaskListTable",
    },
    items: {
      type: Array,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    childFields: {
      type: Array,
      default: () => [],
    },
    hover: {
      type: Boolean,
      default: true,
    },
    noLocalSorting: {
      type: Boolean,
      default: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "No matching records found",
    },
    sortBy: {
      type: String,
      default: "dueDate",
    },
    isSortDirDesc: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    isAllSelected: {
      type: Boolean,
      default: false,
    },
    markAsOptions: {
      type: Array,
      default: () => ["NotStarted", "InProgress", "Complete"],
    },
    taskDueDateDisplayFormat: {
      type: String,
      default: "ddd MMM D YYYY",
    },
    currentRouteName: {
      type: String,
      default: "",
    },
    isChildTable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentYear: moment().year(),
    }
  },
  computed: {
    groupSelectionStates() {
      const states = {}
      this.items.forEach(item => {
        if (item.is_group && item.tasks) {
          const selectedChildCount = item.tasks.filter(childTask =>
            this.selectedItems.find(e => e.handle === childTask.handle)
          ).length

          if (selectedChildCount === 0) {
            states[item.handle] = "none"
          } else if (selectedChildCount === item.tasks.length) {
            states[item.handle] = "all"
          } else {
            states[item.handle] = "partial"
          }
        }
      })
      return states
    },
    localSortBy: {
      get() {
        return this.sortBy
      },
      set(value) {
        this.$emit("update:sort-by", value)
      },
    },
    localIsSortDirDesc: {
      get() {
        return this.isSortDirDesc
      },
      set(value) {
        this.$emit("update:sort-desc", value)
      },
    },
  },
  methods: {
    getTrClass(item) {
      if (!item) return ""
      return item.is_child ? "child-row" : ""
    },
    toggleGroupDetails(item, index) {
      this.$emit("row-clicked", item, index)
    },
    onRowClicked(item, index, event) {
      this.$emit("row-clicked", item, index, event)
    },
    openTaskDrawer(item, index) {
      this.$emit("edit-record", item, index)
    },
    onSelectAllChange(value) {
      this.$emit("select-all-change", value)
    },
    onSelectRowChange(value, item) {
      if (item.is_group) {
        if (value) {
          item.tasks.forEach(childTask => {
            this.$emit("select-row-change", true, childTask)
          })
        } else {
          item.tasks.forEach(childTask => {
            this.$emit("select-row-change", false, childTask)
          })
        }
      } else {
        this.$emit("select-row-change", value, item)
      }
    },
    onEditRecord(item, index) {
      this.$emit("edit-record", item, index)
    },
    onDeleteOrRestoreTasks(items, restore) {
      this.$emit("delete-or-restore-tasks", items, restore)
    },
    onUpdateTask(item, index, updatedFields) {
      this.$emit("update-task", item, index, updatedFields)
    },
    isItemSelected(item) {
      if (item.is_group) {
        return (
          item.tasks &&
          item.tasks.length > 0 &&
          item.tasks.every(childTask =>
            this.selectedItems.find(e => e.handle === childTask.handle)
          )
        )
      }
      return !!this.selectedItems.find(e => e.handle === item.handle)
    },
    hasNotes(item) {
      return !!(item.description && parseLatestNote(item.description))
    },
    getNote(item) {
      return parseLatestNote(item.description) || { header: "", content: "" }
    },
    getPopoverTargetId(item, index) {
      const base = item.id || item.handle || `idx-${index}`
      return String(base).replace(/[^a-zA-Z0-9_-]/g, "_")
    },
    formatDueDate(date) {
      return formatTaskDueDate(date, this.$tenantTimezone, this.currentYear)
    },
    formatUpdatedDate(date) {
      if (!date) return ""
      const m = moment(date).tz(this.$tenantTimezone || "America/Chicago")
      if (m.year() === this.currentYear) {
        return m.format("MMM D")
      }
      return m.format("MMM D, YYYY")
    },
    getRoleDisplay(item) {
      if (!item.role) return ""
      if (typeof item.role === "string") return item.role
      return item.role.roleHandle || item.role.name || ""
    },
    getRoleTooltip(item) {
      if (!item.role) return ""
      if (typeof item.role === "string") return item.role
      return item.role.name || item.role.roleHandle || ""
    },
    resolveTaskStatusVariant(status) {
      if (status === "NotStarted") return "secondary"
      if (status === "Complete") return "success"
      if (status === "InProgress") return "info"
      if (status === "Exported") return "warning"
      return "primary"
    },
    splitCamelCaseWords(text) {
      const result = text.replace(/([A-Z])/g, " $1")
      return result.charAt(0).toUpperCase() + result.slice(1)
    },
    truncateTaskName(name, wordLimit = 50) {
      if (!name) return ""
      const words = name.trim().split(/\s+/)
      if (words.length > wordLimit) {
        return words.slice(0, wordLimit).join(" ") + "..."
      }
      return name
    },
    isTaskNameTruncated(name, wordLimit = 50) {
      if (!name) return false
      const words = name.trim().split(/\s+/)
      return words.length > wordLimit
    },
  },
}
</script>

<style scoped lang="scss">
.child-table {
  margin: 0 !important;
}

.child-table table {
  margin-bottom: 0 !important;
}

.child-table .table td {
  padding: 0.5rem 0.75rem !important;
  border-top: none !important;
}

.child-table .table tr {
  background-color: #f8f9fa !important;
}

.child-table .table tr:hover {
  background-color: #e9ecef !important;
}

.group-toggle-btn {
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:focus {
    outline: none;
  }
}

.select-cell-wrapper {
  max-width: fit-content;
}

.child-row-checkbox {
  margin-left: 1.25rem;
}

.role-chip {
  display: inline-flex;
  align-items: center;
  background-color: #f1f2f4;
  border: 1px solid #dcdfe4;
  border-radius: 4px;
  padding: 0.15rem 0.45rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #5e5873;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
}

.notes-preview-popover {
  max-width: 320px;
}

.notes-popover-content {
  padding: 0.25rem 0;
}

.notes-header {
  font-size: 0.78rem;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 0.35rem;
}

.notes-body-text {
  font-size: 0.82rem;
  color: #2e384d;
  line-height: 1.4;
  max-height: 140px;
  overflow-y: auto;
}

.notes-open-link {
  font-size: 0.78rem;
  color: #193a9a;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    text-decoration: underline !important;
    color: #122766;
  }
}

.row-actions-dropdown {
  ::v-deep .dropdown-toggle {
    padding: 0;
    border: none;
    background: transparent !important;
    box-shadow: none !important;
    &::after {
      display: none;
    }
  }
}

.dark-muted-text {
  color: #5e5873 !important;
}

.task-name-text {
  white-space: nowrap;
}
</style>

<style lang="scss">
.task-table {
  margin-bottom: 0;

  thead th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    border-top: none;
    border-bottom: 1px solid #ebe9f1;
    padding: 0.75rem 1rem !important;
    background-color: #ffffff;
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #fafafc !important;
    }

    td {
      padding: 0.75rem 1rem !important;
      vertical-align: middle !important;
      border-top: 1px solid #ebe9f1;
    }
  }

  .child-row {
    background-color: #f8f9fa !important;
    &:hover {
      background-color: #f1f2f4 !important;
    }
    td {
      border-top: 1px solid #f1f2f4 !important;
    }
  }
}

.dark-layout {
  .task-table {
    thead th {
      background-color: #283046;
      border-color: #3b4253;
      color: #b4b7bd;
    }
    tbody tr td {
      border-color: #3b4253;
    }
    tbody tr:hover {
      background-color: #161d31 !important;
    }
    .child-row {
      background-color: #242b3d !important;
      &:hover {
        background-color: #1e2433 !important;
      }
    }
  }
  .role-chip {
    background-color: #283046;
    border-color: #3b4253;
    color: #d0d2d6;
  }
  .notes-popover-content {
    color: #d0d2d6;
  }
  .notes-header {
    color: #b4b7bd;
  }
  .notes-body-text {
    color: #d0d2d6;
  }
  .notes-open-link {
    color: #7367f0;
  }
  .dark-muted-text {
    color: #d0d2d6 !important;
  }
}
</style>
